import React from 'react'
import { useApp } from '../context/AppContext'
import { api } from '../api/client'
import { useDialog } from '../hooks/useDialog'
import { useCardAccount } from '../payments/useCardAccount'
import { cardPaymentsShown } from '../payments/visibility'
import { menuBadges } from './menuBadges'
import { GROUPS } from '../screens/settings/SettingsGroup'
import { Icon, IconButton } from '../ui/Icon'
import './Sheet.css'
import '../screens/settings/Settings.css'


export default function Menu() {
  const { state, dispatch } = useApp()
  const [signingOut, setSigningOut] = React.useState(false)
  // The plan and payments page appears once the server says card payments
  // are configured (Ruling 14), or once it reports her plan.
  const showCard = cardPaymentsShown(useCardAccount())
  const showPlan = !!state.me?.billing
  const groups = GROUPS.filter((g) => g.id !== 'payments' || showCard || showPlan)

  const business = state.me?.business
  // Real figures only; a badge that can't load is simply not shown.
  const [badges, setBadges] = React.useState<string[]>([])
  React.useEffect(() => {
    let live = true
    Promise.all([api.listReviews(), api.listServices()])
      .then(([reviews, services]) => { if (live) setBadges(menuBadges(reviews, services)) })
      .catch(() => {})
    return () => { live = false }
  }, [])
  const location = [business?.address_line1, business?.city].filter(Boolean).join(', ')

  const dialogRef = React.useRef<HTMLDivElement>(null)
  useDialog(dialogRef, () => dispatch({ type: 'CLOSE_MENU' }), { closable: !signingOut })

  const signOut = async () => {
    setSigningOut(true)
    try {
      await api.logout()
    } catch {
      // Still sign out on this device; the session expires on its own.
    }
    dispatch({ type: 'CLOSE_MENU' })
    dispatch({ type: 'SESSION_ENDED' })
  }

  return (
    <div ref={dialogRef} className="menu-overlay" role="dialog" aria-modal="true" aria-labelledby="menu-title">
      <div className="menu-header">
        <h1 id="menu-title" className="menu-header__title">Settings</h1>
        <IconButton
          icon="close"
          variant="filled"
          className="menu-close"
          label="Close settings"
          onClick={() => dispatch({ type: 'CLOSE_MENU' })}
        />
      </div>

      <div className="menu-content">
        <h2 className="menu-title">
          {business?.trading_name ?? state.me?.professional.display_name}
        </h2>
        {location && <div className="menu-subtitle">{location}</div>}

        {badges.length > 0 && (
          <div className="menu-badges">
            {badges.map((b) => <span key={b} className="tag">{b}</span>)}
          </div>
        )}

        <div className="settings-rows menu-rows">
          {groups.map((g) => (
            <button
              key={g.id}
              type="button"
              className="settings-row"
              onClick={() => dispatch({ type: 'OPEN_SETTINGS_PANEL', payload: g.panels[0] })}
            >
              <span className="settings-row__title">{g.title}</span>
              <span className="settings-row__detail">{g.detail}</span>
              <Icon name="chevron-right" size={18} className="settings-row__chevron" />
            </button>
          ))}
        </div>

        <div className="settings-section-label">Account</div>
        <div className="settings-rows">
          <button
            type="button"
            className="settings-row"
            onClick={() => dispatch({ type: 'OPEN_SETTINGS_PANEL', payload: 'privacy' })}
          >
            <span className="settings-row__title">Privacy & records</span>
            <span className="settings-row__detail">Old records, your clients’ privacy notice, your data and closing your account</span>
            <Icon name="chevron-right" size={18} className="settings-row__chevron" />
          </button>
          {/* The one way to reach us (review 2026-10-14): errors that can't be
              fixed here point to this address too. */}
          <a className="settings-row" href="mailto:hello@harmovi.com">
            <span className="settings-row__title">Help</span>
            <span className="settings-row__detail">Questions or problems? Email hello@harmovi.com</span>
            <Icon name="chevron-right" size={18} className="settings-row__chevron" />
          </a>
        </div>
        <button
          type="button"
          className="btn btn-secondary btn-block menu-signout"
          disabled={signingOut}
          onClick={signOut}
        >
          {signingOut ? 'Signing out…' : 'Sign out'}
        </button>

        <p className="menu-footer">Build the beauty business that fits your life.</p>
        {/* The published legal pages (A12, 2026-10-29). The agreements she
            accepted are the same documents, linked from the Agreements screen. */}
        <p className="menu-footer">
          <a href="https://harmovi.com/legal/privacy" target="_blank" rel="noreferrer">Privacy</a>
          {' · '}
          <a href="https://harmovi.com/legal/terms" target="_blank" rel="noreferrer">Terms</a>
        </p>
      </div>
    </div>
  )
}
